import {
  pharmacyService,
  type PharmacyInfoResponse,
  type PharmacySearchRequest,
  type PharmacyUpdateRequest,
} from '@/entities/pharmacy/api/pharmacy.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const pharmacyKeys = {
  all: ['pharmacies'] as const,
  lists: () => [...pharmacyKeys.all, 'list'] as const,
  list: (search: PharmacySearchRequest, pageable: Pageable) =>
    [...pharmacyKeys.lists(), search, pageable] as const,
  details: () => [...pharmacyKeys.all, 'detail'] as const,
  detail: (pharmacyId: number) =>
    [...pharmacyKeys.details(), pharmacyId] as const,
};

export interface PharmacyListParams {
  search: PharmacySearchRequest;
  pageable: Pageable;
}

/**
 * 문전약국 목록 조회.
 * 레거시 `pharmacyInfo.js` — 약국명·관련 병의원·영업상태 필터 + 서버 페이지네이션.
 */
export const usePharmacyList = ({ search, pageable }: PharmacyListParams) =>
  usePaginatedQuery<PharmacySearchRequest, PharmacyInfoResponse>({
    queryKeyPrefix: pharmacyKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      pharmacyService.getPharmacies(s, p),
  });

/**
 * 문전약국 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportPharmacies = () =>
  useMutation({
    mutationFn: (search: PharmacySearchRequest) =>
      pharmacyService.exportPharmacies(search),
  });

/** 문전약국 상세 조회 — 레거시 `pharmacyInfoDetails.js` */
export const usePharmacyDetail = (pharmacyId: number | null) =>
  useQuery({
    queryKey: pharmacyId != null ? pharmacyKeys.detail(pharmacyId) : [],
    queryFn: async () => {
      if (pharmacyId == null) throw new Error('pharmacyId is required');
      return pharmacyService.getPharmacyDetail(pharmacyId);
    },
    enabled: pharmacyId != null,
  });

export const useUpdatePharmacy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pharmacyId,
      data,
    }: {
      pharmacyId: number;
      data: PharmacyUpdateRequest;
    }) => pharmacyService.updatePharmacy(pharmacyId, data),
    onSuccess: (_, { pharmacyId }) => {
      queryClient.invalidateQueries({ queryKey: pharmacyKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: pharmacyKeys.detail(pharmacyId),
      });
    },
  });
};
